export interface RgbaColor {
  r: number
  g: number
  b: number
  a: number
}

export interface CanvasBounds {
  left: number
  top: number
  width: number
  height: number
}

export interface PixelCoordinate {
  x: number
  y: number
}

export interface ColorFormats {
  hex: string
  rgb: string
  hsl: string
  hsv: string
  alpha: number
  transparent: boolean
}

export interface LoupeSampleRect {
  sourceX: number
  sourceY: number
  sourceWidth: number
  sourceHeight: number
  destinationX: number
  destinationY: number
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function byteToHex(value: number): string {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0').toUpperCase()
}

function formatAlpha(alpha: number): string {
  return (clamp(alpha, 0, 255) / 255).toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
}

function rgbToHue(r: number, g: number, b: number, max: number, delta: number): number {
  if (delta === 0) return 0
  if (max === r) return 60 * (((g - b) / delta) % 6)
  if (max === g) return 60 * ((b - r) / delta + 2)
  return 60 * ((r - g) / delta + 4)
}

export function colorFormats(color: RgbaColor): ColorFormats {
  const r = clamp(Math.round(color.r), 0, 255)
  const g = clamp(Math.round(color.g), 0, 255)
  const b = clamp(Math.round(color.b), 0, 255)
  const a = clamp(Math.round(color.a), 0, 255)
  const red = r / 255
  const green = g / 255
  const blue = b / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const delta = max - min
  const lightness = (max + min) / 2
  const hue = (rgbToHue(red, green, blue, max, delta) + 360) % 360
  const hslSaturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1))
  const hsvSaturation = max === 0 ? 0 : delta / max
  const roundedHue = Math.round(hue)
  const hslS = Math.round(hslSaturation * 100)
  const hslL = Math.round(lightness * 100)
  const hsvS = Math.round(hsvSaturation * 100)
  const hsvV = Math.round(max * 100)
  const alpha = formatAlpha(a)
  const opaque = a === 255

  return {
    hex: `#${byteToHex(r)}${byteToHex(g)}${byteToHex(b)}${opaque ? '' : byteToHex(a)}`,
    rgb: opaque ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha})`,
    hsl: opaque
      ? `hsl(${roundedHue}, ${hslS}%, ${hslL}%)`
      : `hsla(${roundedHue}, ${hslS}%, ${hslL}%, ${alpha})`,
    hsv: opaque
      ? `hsv(${roundedHue}, ${hsvS}%, ${hsvV}%)`
      : `hsva(${roundedHue}, ${hsvS}%, ${hsvV}%, ${alpha})`,
    alpha: a / 255,
    transparent: a === 0,
  }
}

export function pointerToPixel(
  clientX: number,
  clientY: number,
  bounds: CanvasBounds,
  imageWidth: number,
  imageHeight: number,
): PixelCoordinate | null {
  if (
    bounds.width <= 0 ||
    bounds.height <= 0 ||
    imageWidth <= 0 ||
    imageHeight <= 0 ||
    clientX < bounds.left ||
    clientY < bounds.top ||
    clientX > bounds.left + bounds.width ||
    clientY > bounds.top + bounds.height
  ) {
    return null
  }

  const normalizedX = (clientX - bounds.left) / bounds.width
  const normalizedY = (clientY - bounds.top) / bounds.height
  return {
    x: clamp(Math.floor(normalizedX * imageWidth), 0, imageWidth - 1),
    y: clamp(Math.floor(normalizedY * imageHeight), 0, imageHeight - 1),
  }
}

export function clampPixel(
  x: number,
  y: number,
  imageWidth: number,
  imageHeight: number,
): PixelCoordinate {
  return {
    x: Math.max(0, Math.min(Math.round(x), imageWidth - 1)),
    y: Math.max(0, Math.min(Math.round(y), imageHeight - 1)),
  }
}

export function keyboardPixelStep(
  pixel: PixelCoordinate,
  dx: number,
  dy: number,
  imageWidth: number,
  imageHeight: number,
): PixelCoordinate {
  return clampPixel(pixel.x + dx, pixel.y + dy, imageWidth, imageHeight)
}

export function loupeSampleRect(
  coordinate: PixelCoordinate,
  imageWidth: number,
  imageHeight: number,
  sampleSize = 11,
): LoupeSampleRect {
  const radius = Math.floor(sampleSize / 2)
  const windowLeft = coordinate.x - radius
  const windowTop = coordinate.y - radius
  const sourceX = clamp(windowLeft, 0, imageWidth)
  const sourceY = clamp(windowTop, 0, imageHeight)
  const sourceRight = clamp(windowLeft + sampleSize, 0, imageWidth)
  const sourceBottom = clamp(windowTop + sampleSize, 0, imageHeight)

  return {
    sourceX,
    sourceY,
    sourceWidth: Math.max(0, sourceRight - sourceX),
    sourceHeight: Math.max(0, sourceBottom - sourceY),
    destinationX: sourceX - windowLeft,
    destinationY: sourceY - windowTop,
  }
}
